/**
 * JarvisSettings
 *
 * Settings-Karte für die KI-Zentrale, mit der HeyKiki-Sub-Org-Admins den
 * Jarvis-Modus ein/ausschalten können. Sichtbar nur wenn:
 *   - User ist in einer HEYKIKI-Org
 *   - Diese Org ist in `JARVIS_BETA_ORG_IDS` whitelisted (Backend liefert
 *     `betaAllowed: true` im /jarvis/status Response)
 *
 * Standalone-Orgs und nicht-whitelisted HeyKiki-Subs sehen die Karte nicht
 * (rendert null) — kein versehentliches Aktivieren während der Test-Phase.
 */

import { useEffect, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useAuth } from '../../../context/AuthContext';
import { clearJarvisStatusCache } from './useJarvisStatus';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';

const GREETING_OPTIONS = [
  { value: 'OFF', label: 'Aus (keine Begrüßung)' },
  { value: 'SHORT', label: 'Kurz (1 Satz)' },
  { value: 'FULL', label: 'Voll (Termine + To-Dos)' },
];

export default function JarvisSettings() {
  const { user } = useAuth();
  const isHeykikiOrg = user?.organisation?.orgType === 'HEYKIKI';
  const [status, setStatus] = useState(null);
  const [saving, setSaving] = useState(false);

  // Status laden
  useEffect(() => {
    if (!isHeykikiOrg) return;
    const token = localStorage.getItem('token');
    if (!token) return;
    axios
      .get(`${API_URL}/heykiki/jarvis/status`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setStatus(r.data))
      .catch(() => setStatus({ aktiv: false, betaAllowed: false }));
  }, [isHeykikiOrg]);

  // Sichtbarkeits-Gate: andere Org-Typen oder nicht-Beta = nichts rendern.
  if (!isHeykikiOrg) return null;
  if (!status) return null;
  if (!status.betaAllowed) return null;

  const patch = async (changes) => {
    const token = localStorage.getItem('token');
    if (!token) return;
    setSaving(true);
    try {
      const r = await axios.patch(`${API_URL}/heykiki/jarvis/settings`, changes, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStatus(r.data);
      // Header live updaten: dispatched die neue Payload, useJarvisStatus
      // im JarvisAvatarHeader picked das auf und rerendert sofort
      // (kein Page-Reload nötig).
      window.dispatchEvent(new CustomEvent('jarvis-status-changed', { detail: r.data }));
      toast.success('MyKiki-Einstellungen gespeichert');
    } catch (e) {
      const msg = e.response?.data?.error || e.message;
      toast.error(`Speichern fehlgeschlagen: ${msg}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-surface border border-border rounded-lg p-6 my-4">
      <div className="flex items-start gap-4 mb-4">
        <div className="flex-1">
          <h3 className="text-lg font-bold text-text mb-1">
            MyKiki — interne Voice-Assistentin <span className="text-xs uppercase bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded ml-2">Beta</span>
          </h3>
          <p className="text-sm text-text-muted">
            Aktiviert den animierten Kiki-Avatar im Header. Klick startet eine Voice-Session,
            in der Kiki dein CRM per Sprache steuert (Termine, Anfragen, Übersichten).
          </p>
        </div>
      </div>

      {/* Haupt-Toggle */}
      <label className="flex items-center justify-between py-3 border-t border-border cursor-pointer">
        <div>
          <div className="font-medium text-text">MyKiki aktivieren</div>
          <div className="text-sm text-text-muted">
            Bei „aus" bleibt das statische HeyKiki-Logo im Header.
          </div>
        </div>
        <input
          type="checkbox"
          checked={!!status.aktiv}
          disabled={saving}
          onChange={(e) => patch({ jarvisAktiv: e.target.checked })}
          className="w-5 h-5 accent-primary cursor-pointer disabled:opacity-50"
        />
      </label>

      {/* Login-Begrüßung */}
      <div className="py-3 border-t border-border">
        <div className="flex items-center justify-between">
          <div>
            <div className="font-medium text-text">Begrüßung beim Login</div>
            <div className="text-sm text-text-muted">
              Kiki begrüßt dich beim Login und nennt offene Termine bzw. Aufgaben.
            </div>
          </div>
          <select
            value={status.greetingMode || 'OFF'}
            disabled={saving || !status.aktiv}
            onChange={(e) => patch({ jarvisGreetingMode: e.target.value })}
            className="ml-4 px-3 py-2 border border-border rounded-md bg-surface text-text disabled:opacity-50"
          >
            {GREETING_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
      </div>

    </div>
  );
}
